import React, { useState } from 'react';

import { schemas } from '@recurrency/core-api-schema';
import { TenantIntegrationDTO } from '@recurrency/core-api-schema/dist/integrations/tenantIntegrationDTO';
import { Alert, Form, Input, message } from 'antd';

import { Button } from 'components/Button';
import { InputFormItem } from 'components/FormItems';
import { Modal } from 'components/Modal';
import { ConfirmModal } from 'components/Modal/ConfirmModal';

import { useGlobalApp } from 'hooks/useGlobalApp';

import { coreApiFetch } from 'utils/api';
import { showAsyncModal } from 'utils/asyncModal';
import { captureAndShowError } from 'utils/error';

interface TransactionApiFormValues {
  api_host: string;
  api_transaction_consumer_key?: string;
  api_username?: string;
  api_password?: string;
}

function trimToUndefined(value?: string): string | undefined {
  const trimmed = value?.trim();
  return trimmed || undefined;
}

function normalizeApiHost(raw: string): string {
  const trimmed = raw.trim();
  const withScheme = trimmed.includes('://') ? trimmed : `https://${trimmed}`;
  return withScheme.replace(/\/+$/, '');
}

export function UpdateTransactionApiModal({
  integration,
  onClose,
  onSuccess,
}: {
  integration: TenantIntegrationDTO;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { activeTenant } = useGlobalApp();
  const [form] = Form.useForm<TransactionApiFormValues>();
  const [isLoading, setIsLoading] = useState(false);

  const validateAuthMethod = () => {
    const token = trimToUndefined(form.getFieldValue('api_transaction_consumer_key'));
    const username = trimToUndefined(form.getFieldValue('api_username'));
    const password = trimToUndefined(form.getFieldValue('api_password'));
    if (token || (username && password)) {
      return Promise.resolve();
    }
    return Promise.reject(new Error('Provide a consumer key or both API username and password'));
  };

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();

      const confirmed = await showAsyncModal(ConfirmModal, {
        title: 'Confirm Transaction API Update',
        content: (
          <div>
            <Alert
              type="warning"
              message="This action will update your Transaction API credentials"
              description={
                <>
                  <p>New API credentials will be used for all future P21 Transaction API exports.</p>
                  <ul>
                    <li>Database credentials are not changed</li>
                    <li>A consumer key is preferred; username and password are optional</li>
                  </ul>
                  <p>
                    <strong>Are you sure you want to proceed?</strong>
                  </p>
                </>
              }
              showIcon
            />
          </div>
        ),
        okText: 'Update Transaction API Credentials',
        okType: 'primary',
        cancelText: 'Cancel',
        width: 600,
      });

      if (!confirmed) {
        return;
      }

      setIsLoading(true);

      const token = trimToUndefined(values.api_transaction_consumer_key);
      const username = trimToUndefined(values.api_username);
      const password = trimToUndefined(values.api_password);
      const secrets: TransactionApiFormValues = {
        api_host: normalizeApiHost(values.api_host),
      };
      if (token) {
        secrets.api_transaction_consumer_key = token;
      }
      if (username) {
        secrets.api_username = username;
      }
      if (password) {
        secrets.api_password = password;
      }

      // Consumer key is on TenantIntegrationSecrets in core-api; published frontend
      // schema may lag until that package is bumped after the core-api merge.
      await coreApiFetch(schemas.integrations.patchUpdatePartialIntegration, {
        pathParams: {
          tenantId: activeTenant.id,
          integrationId: integration.id,
        },
        bodyParams: {
          secrets,
        } as Any,
      });

      message.success('Transaction API credentials updated successfully!');
      form.resetFields();
      onSuccess();
      onClose();
    } catch (error: Any) {
      if (error.errorFields) {
        return;
      }
      captureAndShowError(error, 'Unable to update Transaction API credentials');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal
      visible
      width="600px"
      title="Update Transaction API Credentials"
      onCancel={onClose}
      centered
      footer={[
        <Button key="cancel" onClick={onClose}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={isLoading} onClick={handleSubmit}>
          Update Transaction API Credentials
        </Button>,
      ]}
    >
      <Alert
        type="info"
        message="For security reasons, current credentials are not displayed. Enter the Middleware URL and either a consumer key or an API username and password."
        style={{ marginBottom: 16 }}
        showIcon
      />
      <Form form={form} layout="vertical">
        <InputFormItem
          name="api_host"
          label="API Host"
          placeholder="e.g., https://youraccountname.epicordistribution.com"
          rules={[
            { required: true, message: 'API host is required' },
            {
              validator: (_: unknown, value: string) => {
                const raw = value?.trim();
                if (!raw) {
                  return Promise.resolve();
                }
                try {
                  const url = new URL(raw.includes('://') ? raw : `https://${raw}`);
                  if (!url.hostname) {
                    throw new Error('missing hostname');
                  }
                  return Promise.resolve();
                } catch {
                  return Promise.reject(
                    new Error('Enter a valid URL, e.g. https://youraccountname.epicordistribution.com'),
                  );
                }
              },
            },
          ]}
        />
        <Form.Item
          name="api_transaction_consumer_key"
          label="Transaction API Token (Consumer Key)"
          dependencies={['api_username', 'api_password']}
          rules={[{ validator: validateAuthMethod }]}
        >
          <Input.Password placeholder="Paste the consumer key from Middleware Admin → API Console" />
        </Form.Item>
        <Form.Item
          name="api_username"
          label="API Username"
          dependencies={['api_transaction_consumer_key', 'api_password']}
          rules={[{ validator: validateAuthMethod }]}
        >
          <Input placeholder="e.g., YOURCOMPANY.RECURRENCY, USER.RECURRENCY" />
        </Form.Item>
        <Form.Item
          name="api_password"
          label="API Password"
          dependencies={['api_transaction_consumer_key', 'api_username']}
          rules={[{ validator: validateAuthMethod }]}
        >
          <Input.Password placeholder="API user password" />
        </Form.Item>
      </Form>
    </Modal>
  );
}
